/**
 * Minimal JWT expiry check — reads the `exp` claim client-side, no
 * signature verification (that's the backend's job; the token is only
 * ever trusted after the API accepts it on a real request). Used purely
 * to decide "should the UI still treat this as a logged-in session" so
 * an expired token sitting in localStorage isn't treated as valid until
 * the next API call happens to come back 401 — see AUDIT_FIX_LOG.md,
 * admin Critical #2.
 *
 * Not worth a dependency (jwt-decode etc.) for a few lines of base64url
 * decoding.
 */

function base64UrlDecode(input: string): string {
  const base64 = input.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64.padEnd(
    base64.length + ((4 - (base64.length % 4)) % 4),
    '='
  );
  // atob is available in every browser this app targets.
  return decodeURIComponent(
    atob(padded)
      .split('')
      .map(c => '%' + c.charCodeAt(0).toString(16).padStart(2, '0'))
      .join('')
  );
}

/**
 * Returns the token's `exp` claim in epoch milliseconds, or null if the
 * token is malformed or has no expiry claim.
 */
export function getTokenExpiryMs(token: string): number | null {
  try {
    const [, payload] = token.split('.');
    if (!payload) return null;
    const decoded = JSON.parse(base64UrlDecode(payload)) as { exp?: number };
    if (typeof decoded.exp !== 'number') return null;
    return decoded.exp * 1000;
  } catch {
    // Malformed token — treat as unusable rather than throwing during a
    // routine auth check.
    return null;
  }
}

/**
 * True if the token is missing an expiry claim, malformed, or already
 * expired. A token with no readable expiry is treated as expired (fail
 * closed) rather than assumed valid.
 */
export function isTokenExpired(token: string | null | undefined): boolean {
  if (!token) return true;
  const expiryMs = getTokenExpiryMs(token);
  if (expiryMs === null) return true;
  return Date.now() >= expiryMs;
}
